import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Image,
  Stack,
  Icon,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Grid,
  useToast,
  Input,
  Select,
  Circle,
  useBreakpointValue,
  FormControl,
  FormErrorMessage,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import axiosInstance from "../helpers/axiosInstance";
import { FaCheckCircle } from "react-icons/fa";
import aiCreationImage from "../assets/images/for-business-sleep.jpg";
import aiCreationImageMobile from "../assets/images/for-business-sleep-mobile.jpg";
import vibeImage from "../assets/images/for-business-taste.jpg";
import vibeImageMobile from "../assets/images/for-business-taste-mobile.jpg";
import exploreImage from "../assets/images/for-business-explore.jpg";
import Navbar from "../components/Navbar";
import darkLogo from "../assets/images/Newlogo-black.png";
import BackToTopButton from "../components/common/BackToTopButton";
import CreatorsBusinessBanner from "../components/common/CreatorsBusinessBanner";
import Footer from "./Footer";
import FAQBusinessPage from "./FAQBusinessPage";
import Translate from "../components/LanguageTranslation/Translate";
import banner from "../assets/images/for-business-banner.jpg";
import businessProgramImage from "../assets/images/for-business-section-5.jpg";
import PhoneInputComponent from "./common/PhoneInputComponent";
import JoinCommunitySection from "./common/JoinCommunitySection";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../auth";
import { useGoogleAuth } from "./common/google/GoogleAuthContext";
import AuthModal from "./AuthModal";

const BusinessPage = () => {
  const toast = useToast();
  
  // Authentication state
  const [logged] = useAuth();
  const {
    authState: { isLoggedIn },
  } = useGoogleAuth();
  const isUserLoggedIn = logged || isLoggedIn;
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  // State variables to track form inputs
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [websiteOrInstagram, setWebsiteOrInstagram] = useState("");
  const [creator, setCreator] = useState("");
  const [phone, setPhone] = useState(""); // Add state for phone
  const [loading, setLoading] = useState(false);

  // Create refs for the sections
  const sectionOneRef = useRef(null);
  const sectionTwoRef = useRef(null);
  const sectionFiveRef = useRef(null); // New ref for section five

  const scrollToSectionOne = () => {
    if (sectionOneRef.current) {
      sectionOneRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Function to scroll to the second section
  const scrollToSectionTwo = () => {
    if (sectionTwoRef.current) {
      sectionTwoRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Function to scroll to the fifth section
  const scrollToSectionFive = () => {
    if (sectionFiveRef.current) {
      sectionFiveRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [errors, setErrors] = useState({});
  
  // Auto-fill email from localStorage when component mounts
  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);
  
  // Handle auth modal close
  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex =
      /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;

    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email format";
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(phone)) {
      newErrors.phone = "Invalid phone number format";
    }
    if (!creator) newErrors.creator = "Business type is required";
    if (!websiteOrInstagram.trim() || !isValidURL(websiteOrInstagram))
      newErrors.websiteOrInstagram = "Website/Instagram is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Check if user is logged in first
    if (!isUserLoggedIn) {
      setAuthModalOpen(true);
      toast({
        title: "Authentication Required",
        description: "Please log in to submit the business application form.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setLoading(true);
    try {
      // Define payload to send to the backend
      const payload = {
        name,
        email,
        websiteOrInstagram,
        phone,
        creator,
        type: "Business",
        application_date: new Date().toISOString().slice(0, 10),
      };

      // Make the POST request using axios
      const response = await axiosInstance.post("/userform/userforms", payload); // Adjust endpoint as per your backend setup

      if (response.status === 201) {
        setWebsiteOrInstagram("");
        setEmail("");
        setName("");
        setPhone("");
        setCreator("");
        setLoading(false);
        toast({
          description: response?.data?.message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        // You can clear the form or add additional success handling here
      }
    } catch (error) {
      setLoading(false);
      console.error("Error submitting form:", error);
      toast({
        description: error?.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const vibeImageSrc = useBreakpointValue({
    base: vibeImageMobile, // for mobile devices
    md: vibeImage, // for medium and larger devices
  });
  const aiCreationImageSrc = useBreakpointValue({
    base: aiCreationImageMobile, // for mobile devices
    md: aiCreationImage, // for medium and larger devices
  });

  return (
    <>
      <Helmet>
        <title>
          Your Online Travel Partner | NOMAD360 for Outdoor Adventures,
          Accommodations, Transport & Travel Services
        </title>
        <meta
          name="description"
          content="Join NOMAD360's AI-powered travel platform. Connect with outdoor enthusiasts, boost bookings, and grow your business with targeted exposure and insights."
        />
        <meta
          name="description:mobile"
          content="Connect with outdoor enthusiasts on NOMAD360. Boost bookings and grow your business with AI-powered travel solutions."
        />
      </Helmet>

      <Box position="absolute" top={0} left={0} width="100%" zIndex="2">
        <Navbar
          bgColor="white"
          linkColor="black"
          styling="outline"
          logoSrc={darkLogo}
          userProfileBgColor="transparaent"
          languageCodeColor="black"
        />
        <CreatorsBusinessBanner heading="For Businesses" banner={banner} />
        <Box bg="white" px={{ base: 4, md: 12 }} py={8}>
          {/* Section 1: Sales Growth (Image on Right, Text on Left) */}
          <Flex
            ref={sectionOneRef}
            direction={{ base: "column-reverse", md: "row" }}
            mb={{ base: 20, md: 20 }}
            maxW={{ base: "auto", md: "80vw", lg: "80vw", xl: "80vw" }}
            justifyContent="center"
            mx="auto"
            h={{ base: "auto", md: "auto", lg: "auto", xl: "auto" }} // Responsive height
          >
            <VStack spacing={0}>
              <Flex
                w="100%"
                // bg="#EBEAF2"
                h={{ base: "auto", md: "auto", lg: "auto", xl: "auto" }}
                alignItems="flex-start"
                justify="space-between"
                direction={{ base: "column", md: "row" }}
              >
                {/* Text Section */}
                <Box
                  flex="1"
                  borderRadius="lg"
                  p={{ base: 6, md: 6 }}
                  pl={{ base: 6, md: 6 }}
                  pb={{ base: 6, md: 24 }}
                  pt={0}
                  textAlign={{ base: "center", md: "left" }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  h="100%"
                >
                  <Heading
                    fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                    w={{ base: "100%", md: "100%", lg: "80%" }}
                    textAlign="left"
                  >
                    <Translate text="Your Online" />{" "}
                    <Text
                      as="span"
                      bgGradient="linear(to-r, #286328, #A8E063)" // Changed to the new gradient colors
                      bgClip="text"
                      fontWeight="bold"
                    >
                      <Translate text="Travel Partner" />
                    </Text>
                  </Heading>
                  <Text
                    mt={{ base: 2, md: 4 }}
                    fontSize={{ base: "md", md: "lg" }}
                    textAlign="left"
                    color="gray.600"
                  >
                    <Translate text="Join our AI-powered travel platform and connect with a global audience of travelers. Boost your visibility, increase bookings, and grow your business with targeted exposure and valuable insights. Let’s work together to create unforgettable experiences!" />
                  </Text>
                </Box>
                {/* Form Section */}
                <Box
                  flex="1"
                  borderRadius="xl"
                  overflow="hidden"
                  // boxShadow="md"
                  h="100%"
                  w="100%"
                  minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
                  bgPosition="center"
                  bgSize="cover"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {/* Semi-transparent background overlay for form */}
                  <Box
                    bg="rgba(255, 255, 255, 0.85)"
                    p={{ base: 3, md: 6 }}
                    pl={{ base: 3, md: 0 }}
                    pb={2}
                    borderRadius="lg"
                    boxShadow="lg"
                    w="100%"
                    h="100%"
                  >
                    <VStack spacing={1.5} align="flex-start" w="100%">
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Name" />
                      </Text>
                      <FormControl isInvalid={errors.name}>
                        <Input
                          placeholder={Translate({
                            text: "Your name",
                            as: "string",
                          })}
                          // size="md"
                          // borderRadius="full"
                          borderColor="gray.300"
                          bg="white"
                          w="100%"
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            if (errors.name) setErrors({ ...errors, name: "" });
                          }}
                        />
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        color="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Flex align="center">
                        <Text fontWeight="bold" color="black" mb={0}>
                          <Translate text="Email" />
                        </Text>
                        <Tooltip
                          hasArrow
                          placement="right"
                          label={
                            <Box maxW="xs">
                              <Text mb={0}>
                                <Translate text="If you want to create a business account with a different email, please sign up with another email address." />
                              </Text>
                            </Box>
                          }
                        >
                          <Box aria-label="Email info" color="gray.500" cursor="pointer" ml={2}>
                            <InfoOutlineIcon />
                          </Box>
                        </Tooltip>
                      </Flex>
                      <FormControl isInvalid={errors.email}>
                        <Input
                          placeholder={Translate({
                            text: "Your email address",
                            as: "string",
                          })}
                          // size="lg"
                          // borderRadius="full"
                          borderColor="gray.300"
                          bg={localStorage.getItem("email") ? "gray.100" : "white"}
                          w="100%"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (errors.email)
                              setErrors({ ...errors, email: "" });
                          }}
                          isDisabled={!!localStorage.getItem("email")}
                          cursor={localStorage.getItem("email") ? "not-allowed" : "text"}
                        />
                        <FormErrorMessage>{errors.email}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        color="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Phone Number" />
                      </Text>
                      <FormControl isInvalid={errors.phone}>
                        {/* <Input
                        placeholder={Translate({
                          text: "Your phone number",
                          as: "string",
                        })}
                        borderRadius="full"
                        borderColor={errors.phone ? "red.300" : "gray.300"}
                        bg="white"
                        w="100%"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (errors.phone) setErrors({ ...errors, phone: '' });
                        }}
                      /> */}
                        <PhoneInputComponent
                          phone={phone}
                          setPhone={setPhone}
                          errors={errors}
                          setErrors={setErrors}
                        />
                        <FormErrorMessage>{errors.phone}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        color="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Business Type" />
                      </Text>
                      <FormControl isInvalid={errors.creator}>
                        <Select
                          placeholder={Translate({
                            text: "Choose your primary offering",
                            as: "string",
                          })}
                          // size="lg"
                          // borderRadius="full"
                          borderColor="gray.300"
                          bg="white"
                          w="100%"
                          value={creator}
                          onChange={(e) => {
                            setCreator(e.target.value);
                            if (errors.creator)
                              setErrors({ ...errors, creator: "" });
                          }}
                        >
                          <option value="Accommodations">
                            <Translate text="Accommodations" />
                          </option>
                          <option value="Transportation">
                            <Translate text="Transportation" />
                          </option>
                          <option value="Exploration">
                            <Translate text="Exploration" />
                          </option>
                          <option value="Food & Drinks">
                            <Translate text="Food & Drinks" />
                          </option>
                          <option value="other">
                            <Translate text="Other" />
                          </option>
                        </Select>
                        <FormErrorMessage>{errors.creator}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        color="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Website" />
                      </Text>
                      <FormControl isInvalid={errors.websiteOrInstagram}>
                        <Input
                          placeholder={Translate({
                            text: "URL of your website",
                            as: "string",
                          })}
                          // size="lg"
                          // borderRadius="full"
                          borderColor="gray.300"
                          bg="white"
                          w="100%"
                          value={websiteOrInstagram}
                          onChange={(e) => {
                            setWebsiteOrInstagram(e.target.value);
                            if (errors.websiteOrInstagram)
                              setErrors({ ...errors, websiteOrInstagram: "" });
                          }}
                        />
                        <FormErrorMessage>
                          {errors.websiteOrInstagram}
                        </FormErrorMessage>
                      </FormControl>

                      {/* Button aligned to the right */}
                      <Box w="100%" textAlign="right" mt={3} pr={0}>
                        <Button
                          // bg={isUserLoggedIn ? "#286328" : "gray.400"}
                          bg="#286328"
                          color="white"
                          size="md"
                          borderRadius="lg"
                          transition="transform 0.3s"
                          _hover={isUserLoggedIn ? {
                            transform: "scale(1.1)",
                          } : {}}
                          w={{ base: "100%", md: "auto" }}
                          onClick={handleSubmit} // Submit form on click
                          isLoading={loading}
                          cursor={isUserLoggedIn ? "pointer" : "pointer"}
                        >
                          <Translate text={isUserLoggedIn ? "Submit" : "Submit"} />
                        </Button>
                      </Box>
                    </VStack>
                  </Box>
                </Box>
              </Flex>
            </VStack>
          </Flex>

          {/* Section 2: AI Creation (Image on Left, Text on Right) */}
          <Flex
            ref={sectionTwoRef} // Attach ref to the second section
            direction={{ base: "column", md: "row-reverse" }}
            alignItems="flex-start" // Align content to the left
            justify="space-between"
            mb={20}
            maxW="80vw"
            mx="auto"
            h={{ base: "auto", md: "550px", lg: "400px" }} // Responsive height
          >
            {/* Text Section */}
            <Box
              flex="1"
              borderRadius="lg"
              p={6}
              pr={{ base: 0, md: 6 }}
              pl={{ base: 0, md: 4 }}
              pt={0}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent={{ base: "center", md: "center" }}
              h="100%"
              w={{ base: "100%", md: "auto" }}
              minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
            >
              <Box
                textAlign="center"
                p={2}
                w={{ base: "60%", md: "50%", lg: "30%" }}
                boxShadow="xl"
                borderRadius="full"
              >
                <Text fontSize="sm" color="gray.400" mb={0}>
                  <Translate text="Accommodations" />
                </Text>
              </Box>
              <Heading
                fontSize={{ base: "2xl", md: "3xl", lg: "5xl" }}
                mt={6}
                w={{ base: "100%", md: "100%", lg: "90%" }}
                textAlign="left"
              >
                <Translate text="For" />{" "}
                <Text
                  as="span"
                  bgGradient="linear(to-r, #A8E063, #286328)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  <Translate text="Holiday Rentals, Hotels " />{" "}
                </Text>
                <Translate text="& More" />
              </Heading>
              {/* <Stack
              spacing={2}
              mt={2}
              w={{ base: "100%", md: "100%", lg: "70%" }}
              textAlign="left"
            >
              <Text fontSize={{ base: "sm", md: "sm" }} fontWeight="bold">
                <Box
                  as="span"
                  bg="#E9F5E9"
                  p={1}
                  pl={2}
                  pr={2}
                  borderRadius="md"
                  mr={2}
                >
                  ✔
                </Box>
                <Translate text="Grow your visibility." />
              </Text>
              <Text fontSize={{ base: "sm", md: "sm" }} fontWeight="bold">
                <Box
                  as="span"
                  bg="#E9F5E9"
                  p={1}
                  pl={2}
                  pr={2}
                  borderRadius="md"
                  mr={2}
                >
                  ✔
                </Box>

                <Translate text="Increase Bookings." />
              </Text>
              <Text fontSize={{ base: "sm", md: "sm" }} fontWeight="bold">
               <Icon as={FaCheckCircle} color="#286328" mr={2} />
                <Translate text="Gain more Credibility." />
              </Text>
            </Stack> */}
              <Box
                mt={4}
                display="flex"
                alignItems="center"
                justifyContent="left"
              >
                <Stack mt={2} spacing={4}>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Grow your visibility" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Increase bookings" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Gain more credibility" />
                    </Text>
                  </Flex>
                </Stack>
              </Box>
              {/* <Divider borderColor="gray.400" /> */}
              <Box w="100%" alignContent="center" mt={4}>
                <Button
                  // mt={2}
                  bg="#286328"
                  color="white"
                  transition="transform 0.3s"
                  _hover={{
                    transform: "scale(1.1)",
                  }}
                  borderRadius="lg"
                  w={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "md" }}
                  onClick={scrollToSectionOne}
                >
                  <Translate text="Sign Up" />
                </Button>
              </Box>
            </Box>

            {/* Image Section */}
            <Box
              flex="1"
              // bg="#EBEAF2"
              pl={{ base: 0, md: 6 }}
              pr={{ base: 0, md: 6 }}
              // pt={2}
              // pb={2}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              h="100%"
              minH={{ base: "auto", md: "100%" }} // Ensure responsive min-height
            >
              <Image
                src={aiCreationImageSrc}
                alt="AI Creation"
                boxSize="100%"
                objectFit="cover"
                borderRadius="40px"
              />
            </Box>
          </Flex>

          {/* Section 3: Vibe Features (Image on Left, Text on Right) */}
          <Flex
            direction={{ base: "column", md: "row" }}
            alignItems="flex-start" // Align content to the left
            justify="space-between"
            mb={20}
            maxW="80vw"
            mx="auto"
            h={{ base: "auto", md: "550px", lg: "400px" }} // Responsive height
          >
            {/* Text Section */}
            <Box
              flex="1"
              borderRadius="lg"
              p={6}
              pr={{ base: 0, md: 6 }}
              pl={{ base: 0, md: 4 }}
              pt={0}
              pb={{ base: 4, md: 6 }}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              h="100%"
              minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
              w={{ base: "100%", md: "auto" }}
            >
              <Box
                textAlign="center"
                p={2}
                w={{ base: "60%", md: "50%", lg: "30%" }}
                boxShadow="xl"
                borderRadius="full"
              >
                <Text fontSize="sm" color="gray.400" mb={0}>
                  <Translate text="Exploration" />
                </Text>
              </Box>
              <Heading
                fontSize={{ base: "2xl", md: "3xl", lg: "5xl" }}
                mt={6}
                w={{ base: "100%", md: "100%", lg: "100%" }}
                textAlign="left"
              >
                <Translate text="For" />{" "}
                <Text
                  as="span"
                  bgGradient="linear(to-r, #A8E063, #286328)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  <Translate text="Local Guides, Tour Operators & Travel Agencies" />{" "}
                </Text>
              </Heading>
              <Box
                mt={4}
                display="flex"
                alignItems="center"
                justifyContent="left"
              >
                <Stack mt={2} spacing={4}>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Expand your reach" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Attract more explorers" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Boost engagement" />
                    </Text>
                  </Flex>
                </Stack>
              </Box>
              {/* <Divider borderColor="gray.400" /> */}
              <Box w="100%" alignContent="center" mt={4}>
                <Button
                  // mt={2}
                  bg="#286328"
                  color="white"
                  transition="transform 0.3s"
                  _hover={{
                    transform: "scale(1.1)",
                  }}
                  borderRadius="lg"
                  w={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "md" }}
                  onClick={scrollToSectionOne}
                >
                  <Translate text="Sign Up" />
                </Button>
              </Box>
            </Box>
            <Box
              flex="1"
              // bg="#EBEAF2"
              pt={2}
              pb={2}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              // boxShadow="md"
              h={{ base: "100%", md: "100%" }}
              minH={{ base: "auto", md: "100%" }} // Ensure responsive min-height
            >
              <Image
                src={exploreImage}
                alt="Vibe Creation"
                boxSize="100%"
                objectFit="cover"
                borderRadius="40px"
              />
            </Box>
          </Flex>

          {/* Section 4: AI Creation (Image on Left, Text on Right) */}
          <Flex
            direction={{ base: "column", md: "row-reverse" }}
            alignItems="flex-start" // Align content to the left
            justify="space-between"
            mb={20}
            maxW="80vw"
            mx="auto"
            h={{ base: "auto", md: "550px", lg: "400px" }} // Responsive height
          >
            {/* Text Section */}
            <Box
              flex="1"
              borderRadius="lg"
              p={6}
              pt={0}
              pr={{ base: 0, md: 6 }}
              pl={{ base: 0, md: 6 }}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              h="100%"
              minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
              w={{ base: "100%", md: "auto" }}
            >
              <Box
                textAlign="center"
                p={2}
                w={{ base: "60%", md: "50%", lg: "30%" }}
                boxShadow="xl"
                borderRadius="full"
              >
                <Text fontSize="sm" color="gray.400" mb={0}>
                  <Translate text="Food & Drinks" />
                </Text>
              </Box>
              <Heading
                fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                mt={6}
                w={{ base: "100%", md: "100%", lg: "80%" }}
                textAlign="left"
              >
                <Translate text=" For" />{" "}
                <Text
                  as="span"
                  bgGradient="linear(to-r, #286328, #A8E063)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  <Translate text="Restaurants, Bars & Pubs" />
                </Text>
              </Heading>

              <Box
                mt={4}
                display="flex"
                alignItems="center"
                justifyContent="left"
              >
                <Stack mt={2} spacing={4}>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Promote special offers" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="More reservations" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Cultivate trust" />
                    </Text>
                  </Flex>
                </Stack>
              </Box>
              {/* <Divider borderColor="gray.400" /> */}
              <Box w="100%" alignContent="center" mt={4}>
                <Button
                  // mt={2}
                  bg="#286328"
                  color="white"
                  transition="transform 0.3s"
                  _hover={{
                    transform: "scale(1.1)",
                  }}
                  borderRadius="lg"
                  w={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "md" }}
                  onClick={scrollToSectionOne}
                >
                  <Translate text="Sign Up" />
                </Button>
              </Box>
            </Box>
            <Box
              flex="1"
              // bg="#EBEAF2"
              pl={{ base: 0, md: 6 }}
              pr={{ base: 0, md: 6 }}
              // pt={2}
              // pb={2}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              h="100%"
              minH={{ base: "auto", md: "100%" }}
            >
              <Image
                src={vibeImageSrc}
                alt="Vibe Creation"
                boxSize="100%"
                objectFit="cover"
                borderRadius="40px"
              />
            </Box>
          </Flex>

          {/* Section 5: Vibe Features (Image on Left, Text on Right) */}
          <Flex
            ref={sectionFiveRef}
            direction={{ base: "column", md: "row" }}
            alignItems="flex-start" // Align content to the left
            justify="space-between"
            mb={20}
            maxW="80vw"
            mx="auto"
            h={{ base: "auto", md: "550px", lg: "400px" }} // Responsive height
          >
            {/* Text Section */}
            <Box
              flex="1"
              borderRadius="lg"
              p={6}
              pt={{ base: 0, md: 6 }}
              pr={{ base: 0, md: 6 }}
              pl={{ base: 0, md: 6 }}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              h="100%"
              w={{ base: "100%", md: "auto" }}
              // minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
            >
              <Heading
                fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                mt={6}
                w={{ base: "100%", md: "100%", lg: "100%" }}
                textAlign="left"
              >
                <Translate text="Join Our" />
                <br></br>
                <Text
                  as="span"
                  bgGradient="linear(to-r, #286328, #A8E063)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  <Translate text="Business Program" />
                </Text>
              </Heading>
              <Stack spacing={2} mt={2} mb={1}>
                <Flex
                  bg="white"
                  p={2}
                  borderRadius="xl"
                  boxShadow="md"
                  alignItems="center"
                >
                  <Circle bg="#286328" color="white" size="8" mr={4}>
                    <Text fontWeight="bold" mb={0}>
                      1
                    </Text>
                  </Circle>
                  <Box w="100%">
                    <Accordion
                      allowToggle
                      // defaultIndex={[0]}
                    >
                      <AccordionItem border="none">
                        <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                          <Box
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                            fontSize={{ base: "md", md: "lg" }}
                            mr={2}
                          >
                            <Translate text="Apply" />
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel
                          pl={0}
                          pb={4}
                          color="gray.600"
                          fontSize="sm"
                          textAlign="left"
                        >
                          <Translate text="Fill out the form and submit your application." />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                </Flex>

                <Flex
                  bg="white"
                  p={2}
                  borderRadius="xl"
                  boxShadow="md"
                  alignItems="center"
                >
                  <Circle bg="#286328" color="white" size="8" mr={4}>
                    <Text fontWeight="bold" mb={0}>
                      2
                    </Text>
                  </Circle>
                  <Box w="100%">
                    <Accordion
                      allowToggle
                      // defaultIndex={[0]}
                    >
                      <AccordionItem border="none">
                        <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                          <Box
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                            fontSize={{ base: "md", md: "lg" }}
                            mr={2}
                          >
                            <Translate text="Set up your Business Profile" />
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel
                          pl={0}
                          pb={4}
                          color="gray.600"
                          fontSize="sm"
                          textAlign="left"
                        >
                          <Translate text="Once accepted, you’ll receive the required information and discover how your business can benefit from partnering with our AI Travel Planner." />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                </Flex>

                <Flex
                  bg="white"
                  p={2}
                  borderRadius="xl"
                  boxShadow="md"
                  alignItems="center"
                >
                  <Circle bg="#286328" color="white" size="8" mr={4}>
                    <Text fontWeight="bold" mb={0}>
                      3
                    </Text>
                  </Circle>
                  <Box w="100%">
                    <Accordion
                      allowToggle
                      // defaultIndex={[0]}
                    >
                      <AccordionItem border="none">
                        <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                          <Box
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                            fontSize={{ base: "md", md: "lg" }}
                            mr={2}
                          >
                            <Translate text="Boost your Business" />
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel
                          pl={0}
                          pb={4}
                          color="gray.600"
                          fontSize="sm"
                          textAlign="left"
                        >
                          <Translate text="Boost your visibility, grow your network, and drive more business through our platform." />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                </Flex>
                {/* <Divider borderColor="gray.400" /> */}
                <Box w="100%" alignContent="center" mt={4}>
                  <Button
                    // mt={2}
                    bg="#286328"
                    color="white"
                    transition="transform 0.3s"
                    _hover={{
                      transform: "scale(1.1)",
                    }}
                    borderRadius="lg"
                    w={{ base: "100%", md: "auto" }}
                    size={{ base: "md", md: "md" }}
                    onClick={scrollToSectionOne}
                  >
                    <Translate text="Sign Up" />
                  </Button>
                </Box>
              </Stack>
            </Box>

            <Box
              flex="1"
              // bg="#EBEAF2"
              pt={2}
              pb={2}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              // boxShadow="md"
              h={{ base: "100%", md: "100%" }}
              minH={{ base: "auto", md: "100%" }}
            >
              <Image
                src={businessProgramImage}
                alt="Vibe Creation"
                boxSize="100%"
                objectFit="cover"
                borderRadius="40px"
              />
            </Box>
          </Flex>

          {/* Section 6: FAQ */}
          <FAQBusinessPage />
        </Box>
        <JoinCommunitySection />
        <Footer />
        <BackToTopButton />
        {/* Authentication Modal */}
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={handleAuthModalClose}
          redirectPath="/businesses"
        />
      </Box>
    </>
  );
};

export default BusinessPage;
